import React, { useEffect, useState } from 'react'
import { SimpleLoading } from 'components/LoadingIcon'
import { useJamesApolloQuery } from 'hooks/useJamesApolloQuery'
import useSessionContext from 'hooks/useSessionContext'
import { sortBy } from 'lodash/fp'
import SwipeableViews from 'react-swipeable-views'
import { bindKeyboard, virtualize } from 'react-swipeable-views-utils'
import { modules } from 'shared/constants'
import { personsOnlyTenant } from 'shared/queries'
import { PersonT } from 'types'
import { isNative } from 'utils/platform'
import DailySlideRenderer from './components/DailySlideRenderer'
import { classes } from './styles'

export interface IDailyShiftListSwipeableProps {
  baseDate: string
  displayFull: boolean
  handleSwipeAction: (amount: number) => void
  swipeIndex: number
  swipeAnimation: boolean
  loadingGoToDay: boolean
  finishLoadingGoToDay: () => void
}

const VirtualizeSwipeableViews = bindKeyboard(virtualize(SwipeableViews))

const DailyShiftsList: React.FC<IDailyShiftListSwipeableProps> = ({
  baseDate,
  displayFull,
  handleSwipeAction,
  swipeIndex,
  swipeAnimation,
  loadingGoToDay,
  finishLoadingGoToDay,
}) => {
  const { personIds } = useSessionContext()
  const [personsWithTenant, setPersonsWithTenant] = useState<PersonT[]>()

  const { loading } = useJamesApolloQuery<{ findAllPersons: PersonT[] }>(personsOnlyTenant, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      const personsWithAccess = data.findAllPersons.filter((person) => {
        if (!person.tenant || !person.tenant?.name) return false
        const { tenant } = person

        return tenant?.modules?.includes(modules.shiftPlan)
      })

      setPersonsWithTenant(sortBy('', personsWithAccess) as PersonT[])
    },
  })

  useEffect(() => {
    if (loadingGoToDay) {
      finishLoadingGoToDay()
    }
  }, [finishLoadingGoToDay, loadingGoToDay])

  if (loading || loadingGoToDay) {
    return <SimpleLoading />
  }

  return (
    <VirtualizeSwipeableViews
      animateTransitions={swipeAnimation}
      enableMouseEvents={isNative()}
      index={swipeIndex}
      onChangeIndex={(newIndex, indexLatest) => {
        handleSwipeAction(newIndex < indexLatest ? -1 : 1)
      }}
      overscanSlideAfter={1}
      overscanSlideBefore={1}
      style={classes.fullWitdh}
      slideRenderer={(params) => {
        const isActive = params.index === swipeIndex

        return (
          <div key={params.key} style={isActive ? classes.activeSlide : classes.inactiveSlide}>
            <DailySlideRenderer
              baseDate={baseDate}
              displayFull={displayFull}
              key={params.index}
              params={params}
              personIds={personIds}
              personsWithTenant={personsWithTenant}
            />
          </div>
        )
      }}
    />
  )
}

export default DailyShiftsList
